import { UnseenReviewBar } from "@/components/admin/UnseenReviewBar";
// Admin Classes workspace — single source of truth for class management.
//
// Phase A of the Class Management cleanup:
//   • Removed binary-isolation harness (?sections= flags, per-section boundaries)
//   • Removed Executive Intelligence panel, health icons, readiness score
//   • Removed placeholder "Duplicate: coming soon" and "Delete is guarded" menu items
//   • Removed data-source switch
// Editor still edits only visibility + thumbnail. Phase B will replace it with a
// single tabbed editor covering every field (title, schedule, pricing, capacity,
// instructor, images, description, supplies, visibility, registration settings).
import { useEffect, useMemo, useState } from "react";
import {
  RefreshCw,
  Loader2,
  Upload,
  MoreVertical,
  AlertCircle,
  Copy,
  ExternalLink,
  Eye,
  EyeOff,
  Archive,
  Info,
  UserCheck,
  UserX,
  Users,
  GraduationCap,
  Plus,
  Files,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { friendlyError } from "@/lib/friendlyError";
import { classes as staticClasses, type ClassItem } from "@/data/classes";
import { resolveClassImage, CLASS_SAFE_COLUMNS } from "@/lib/dbClasses";

import {
  useClassRegistry,
  upsertRegistryForSlug,
  slugOf,
  LIFECYCLE_STATUSES,
  STATUS_META,
  type VisibilityStatus,
} from "@/lib/classRegistry";
import AdminErrorBoundary from "@/components/admin/AdminErrorBoundary";
import { ClassEditorDialog, slugify, type ClassEditorRow, type DraftSeed } from "@/components/admin/classes/ClassEditorDialog";
import FutureSessionInterestOverview from "@/components/admin/classes/FutureSessionInterestOverview";
import PricingHealthCard, { auditClass as auditClassPricing } from "@/components/admin/classes/PricingHealthCard";
import InstructorStaffingCard from "@/components/admin/staffing/InstructorStaffingCard";
import { useStaffingBoard } from "@/hooks/useStaffingBoard";
import { isLive as isLiveAssignment, type AssignmentRow } from "@/lib/staffing";
import FinancialReportsNavCard from "@/components/admin/classes/FinancialReportsNavCard";
import { coerceBillingUnit, publicPriceLabel } from "@/lib/classPricing";
import {
  OperationsBoard,
  readinessForRow,
  type BoardRow,
  type BoardCounts,
  type InstructorState as BoardInstructorState,
} from "@/components/admin/classes/OperationsBoard";
import {
  countsAsEnrolled,
  fetchEnrollmentData,
  interestFamiliesFor,
  type RegistrationRow,
  type RegistrationChildRow,
  type WaitlistRow,
} from "@/lib/enrollment";
import { resolveOperationalEnrollment } from "@/lib/operationalEnrollment";
import {
  resolveInstructorsWithClassDefault,
  operationalInstructorState,
  type OperationalAssignmentRow,
} from "@/lib/operationalInstructors";
import { getClassAliases } from "@/lib/classIdentity";
import { useBusinessSettings } from "@/hooks/useBusinessSettings";
import { isWithinReportingWindow } from "@/lib/businessSettings";
import { resolveNextSessionLabel, resolveNextSessionOption } from "@/lib/nextClassSession";
import type { ClassSession } from "@/data/classes";



/* ---------------- Data model ---------------- */

type Source = "database" | "legacy" | "hybrid";

type MergedRow = {
  id: string;
  slug: string;
  title: string;
  ages: string | null;
  category: string | null;
  schedule: string | null;
  price: string | null;
  capacity: number | null;
  /** Silver Sage standard minimum enrollment target (advisory, never automatic). */
  minEnrollment?: number | null;
  source: Source;
  dbId?: string;
  legacy?: ClassItem;
  thumbnail?: string | null;
  description?: string | null;
  instructorId?: string | null;
  managementType?: "silver_sage" | "external_provider";
  pricePerChild?: number | null;
  billingUnit?: string | null;
  classLength?: string | null;
  duration?: string | null;
  /** Raw override from classes.price_display (no fallback). Audit-only. */
  priceDisplayRaw?: string | null;
  /** Admin intentionally hides the billing unit on the public price line. */
  displayOmitsUnitApproved?: boolean;
  /** Canonical ClassSession[] used by the next-session resolver. */
  sessions?: ClassSession[] | null;
  /** Storage-level public gate from the classes table (conflict detection). */
  dbStatus?: "draft" | "coming_soon" | "published" | "archived";
  dbHidden?: boolean | null;
  acceptsRegistrations?: boolean | null;
  /* Operational detail used ONLY by the admin readiness engine. */
  location?: string | null;
  room?: string | null;
  preferredDays?: string | null;
  preferredTime?: string | null;
  /** Operating model: how this class is meant to work (see classReadiness). */
  pricingModel?: "paid" | "free";
  scheduleModel?: "fixed" | "variable";



};


type DbRow = {
  id: string;
  slug: string | null;
  title: string;
  ages: string | null;
  category: string | null;
  schedule: string | null;
  description: string | null;
  price_per_child: number | null;
  price_display: string | null;
  price_display_omits_unit?: boolean | null;
  capacity: number | null;
  status: "draft" | "coming_soon" | "published" | "archived";
  accepts_registrations: boolean | null;
  hidden: boolean | null;
  /** @deprecated migration-only compat; do not read at runtime */
  hidden_from_calendar?: boolean | null;
  thumbnail_url?: string | null;
  instructor_id?: string | null;
  location?: string | null;
  room?: string | null;
  preferred_days?: string | null;
  preferred_time?: string | null;
  management_type?: "silver_sage" | "external_provider" | null;
  billing_unit?: string | null;
  class_length: string | null;
  duration: string | null;
  sessions?: unknown;
  pricing_model?: "paid" | "free" | null;
  schedule_model?: "fixed" | "variable" | null;
};




const fromDb = (row: DbRow): MergedRow => {
  const isExternal = (row.management_type ?? "silver_sage") === "external_provider";
  const hasInstructor = !!row.instructor_id;
  return {
    id: row.id,
    slug: row.slug ?? row.id,
    title: row.title,
    ages: row.ages,
    category: row.category,
    schedule: row.schedule,
    description: row.description,
    price: publicPriceLabel({
      pricePerChild: row.price_per_child,
      billingUnit: coerceBillingUnit(row.billing_unit),
      priceDisplay: row.price_display,
      externalPayment: isExternal,
      hasInstructor: isExternal ? true : hasInstructor,
    }),
    capacity: row.capacity,
    minEnrollment: (row as unknown as { min_enrollment?: number | null }).min_enrollment ?? null,
    source: "database",
    dbId: row.id,
    // Shared resolver: DB thumbnail → migrated legacy image by slug → null.
    // Must match the public catalog (src/lib/dbClasses.ts) so the admin board
    // never shows "No cover image" for a class the public site renders.
    thumbnail: resolveClassImage(row.slug ?? row.id, row.thumbnail_url),
    instructorId: row.instructor_id ?? null,
    managementType: (row.management_type ?? "silver_sage") as "silver_sage" | "external_provider",
    pricePerChild: row.price_per_child,
    billingUnit: row.billing_unit,
    classLength: row.class_length ?? null,
    duration: row.duration ?? null,
    priceDisplayRaw: row.price_display ?? null,
    displayOmitsUnitApproved: !!row.price_display_omits_unit,
    sessions: Array.isArray(row.sessions) ? (row.sessions as ClassSession[]) : null,
    dbStatus: row.status,
    dbHidden: row.hidden,
    acceptsRegistrations: row.accepts_registrations,
    location: row.location ?? null,
    room: row.room ?? null,
    preferredDays: row.preferred_days ?? null,
    preferredTime: row.preferred_time ?? null,
    pricingModel: row.pricing_model ?? "paid",
    scheduleModel: row.schedule_model ?? "fixed",


  };
};




const fromStatic = (item: ClassItem): MergedRow => ({
  id: item.id,
  slug: slugOf(item),
  title: item.title,
  ages: item.ages,
  category: item.category,
  schedule: item.schedule,
  description: item.description ?? null,
  price: item.price,
  capacity: null,
  source: "legacy",
  legacy: item,
  thumbnail: item.image ?? null,
  managementType: item.managementType ?? "silver_sage",
  pricePerChild: item.unitPrice ?? null,
  billingUnit: item.billingUnit ?? null,
  sessions: item.sessions ?? null,
});


type LifecycleFilter = "all" | VisibilityStatus;

/** Quick "what needs me today" lenses, all derived from canonical data. */
type FocusFilter = "all" | "attention" | "instructor_needed" | "low_enrollment" | "pricing_issue";

const FOCUS_LABELS: Record<FocusFilter, string> = {
  all: "All Classes",
  attention: "Needs Attention",
  instructor_needed: "Instructor Needed",
  low_enrollment: "Low Enrollment",
  pricing_issue: "Pricing Issue",
};
type InstructorState = "assigned" | "missing" | "multiple" | "pending" | "external";

/**
 * Operational instructor for a class card — resolved through the shared
 * resolver so the board, roster, editor, and payroll preview never disagree.
 * Historical / replaced / expired / other-session assignments are excluded
 * (they stay untouched in the database for payroll + audit).
 */
function resolveCardInstructor(
  row: MergedRow,
  rows: OperationalAssignmentRow[] | undefined,
  session: { label: string | null; start: Date | null; end: Date | null },
  instructorNames: Map<string, string>,
): { state: InstructorState; name: string | null; note: string | null } {
  // Externally-managed classes never require a Silver Sage instructor.
  if (row.managementType === "external_provider")
    return { state: "external", name: null, note: null };

  const result = resolveInstructorsWithClassDefault(rows, {
    sessionLabel: session.label,
    sessionStart: session.start,
    sessionEnd: session.end,
    // Administrative metadata only — never staffs a session.
    classLevelInstructorId: row.instructorId ?? null,
  });
  const ids = result.instructorIds;

  const state = operationalInstructorState(result) as InstructorState;
  const name =
    ids.length === 1 ? instructorNames.get(ids[0]) ?? null : null;

  let note: string | null = null;
  if (!ids.length && result.classDefaultInstructorId) {
    const defaultName = instructorNames.get(result.classDefaultInstructorId);
    if (defaultName) note = `Class default on file: ${defaultName} — confirm and assign.`;
  }
  return { state, name, note };
}


/* ---------------- Page ---------------- */

function ClassesPageInner() {
  const { user, isAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const registry = useClassRegistry();
  const { settings: businessSettings } = useBusinessSettings();
  const [dbRows, setDbRows] = useState<DbRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [lifecycle, setLifecycle] = useState<LifecycleFilter>("all");
  // Consolidated view: the class board, or the read-only instructor-side view
  // that used to live on /admin/staffing. Neither view writes assignments.
  const [view, setView] = useState<"classes" | "instructors">("classes");
  const [focus, setFocus] = useState<FocusFilter>("all");
  const [editing, setEditing] = useState<MergedRow | null>(null);
  const [creating, setCreating] = useState<null | {
    seed: Partial<DraftSeed>;
    sourceLabel?: string;
    copyRegistrationPeriodsFromSlug?: string;
  }>(null);
  const [duplicating, setDuplicating] = useState<MergedRow | null>(null);
  const [assignments, setAssignments] = useState<Map<string, OperationalAssignmentRow[]>>(new Map());
  const [instructorNames, setInstructorNames] = useState<Map<string, string>>(new Map());
  const [countsByKey, setCountsByKey] = useState<Map<string, BoardCounts>>(new Map());
  const [enrollmentData, setEnrollmentData] = useState<{
    payments: RegistrationRow[];
    children: RegistrationChildRow[];
    interest: WaitlistRow[];
  }>({ payments: [], children: [], interest: [] });
  const [updatedAtBySlug, setUpdatedAtBySlug] = useState<Map<string, string>>(new Map());
  const [interestThresholdBySlug, setInterestThresholdBySlug] = useState<Map<string, number>>(new Map());


  const loadClasses = async () => {
    setLoading(true);
    const { data, error } = await (supabase as any)
      .from("classes")
      .select(
        // hidden_from_calendar intentionally omitted — class_registry.calendar_visible is the sole runtime authority.
        "id,slug,title,ages,category,schedule,description,price_per_child,price_display,price_display_omits_unit,billing_unit,capacity,min_enrollment,status,accepts_registrations,hidden,thumbnail_url,instructor_id,management_type,class_length,duration,sessions,created_at,location,room,preferred_days,preferred_time,pricing_model,schedule_model",

      )

      .order("created_at", { ascending: false });

    if (error) {
      friendlyError({ what: "I couldn't load the class list from the database.", technical: error });
      setDbRows([]);
    } else {
      setDbRows((data ?? []) as DbRow[]);
    }
    setLoading(false);
  };

  // Raw assignment rows keyed by class_id AND class_slug. Filtering to the
  // operationally-valid instructor happens in the shared resolver
  // (src/lib/operationalInstructors.ts) so every admin surface agrees.
  const loadAssignments = async () => {
    const { data } = await (supabase as any)
      .from("instructor_class_assignments")
      .select(
        "class_id,class_slug,instructor_id,status,assignment_kind,session_label,start_date,end_date,temporary_until_replaced",
      );
    const map = new Map<string, OperationalAssignmentRow[]>();
    for (const r of (data ?? []) as OperationalAssignmentRow[]) {
      if (!r.instructor_id) continue;
      for (const k of [r.class_id, r.class_slug].filter(Boolean) as string[]) {
        const list = map.get(k) ?? [];
        list.push(r);
        map.set(k, list);
      }
    }
    setAssignments(map);
  };


  // Raw enrollment data — the class cards derive every count from the canonical
  // operational resolver (src/lib/operationalEnrollment.ts), never from local
  // lifetime tallies. Interest is counted separately and never as registrations.
  const loadCounts = async () => {
    const { payments, interest, children } = await fetchEnrollmentData();
    setEnrollmentData({ payments, children, interest });
    const counts = new Map<string, BoardCounts>();
    const bump = (key: string, patch: Partial<BoardCounts>) => {
      const cur = counts.get(key) ?? {
        enrolled: 0,
        waitlist: 0,
        interest: 0,
        revenueCents: 0,
        financeEnrolled: 0,
        financeRevenueCents: 0,
        nextSessionLabel: null,
        updatedAt: null,
      };
      counts.set(key, {
        ...cur,
        interest: cur.interest + (patch.interest ?? 0),
        revenueCents: cur.revenueCents + (patch.revenueCents ?? 0),
        financeRevenueCents:
          (cur.financeRevenueCents ?? 0) + (patch.financeRevenueCents ?? 0),
      });
    };
    for (const p of payments) {
      if (!p.class_id) continue;
      // Shared canonical predicate — hidden/test/removed/fully-refunded rows never count.
      if (!countsAsEnrolled(p)) continue;
      const withinWindow = isWithinReportingWindow(p.created_at, businessSettings);
      bump(p.class_id, {
        revenueCents: p.amount_paid ?? 0,
        financeRevenueCents: withinWindow ? p.amount_paid ?? 0 : 0,
      });
    }
    for (const i of interest) {
      if (!i.class_id) continue;
      bump(String(i.class_id), { interest: 1 });
    }
    setCountsByKey(counts);
  };

  const loadInstructorNames = async () => {
    const { data } = await (supabase as any)
      .from("instructors")
      .select("id, preferred_name, full_name");
    const map = new Map<string, string>();
    for (const r of (data ?? []) as Array<{
      id: string;
      preferred_name: string | null;
      full_name: string | null;
    }>) {
      const name = r.preferred_name?.trim() || r.full_name?.trim() || "";
      if (name) map.set(r.id, name);
    }
    setInstructorNames(map);
  };

  const loadInterestThresholds = async () => {
    const { data } = await (supabase as any)
      .from("class_interest_thresholds")
      .select("class_slug, threshold, enabled");
    const map = new Map<string, number>();
    for (const r of (data ?? []) as Array<{
      class_slug: string | null;
      threshold: number | null;
      enabled: boolean | null;
    }>) {
      if (!r.class_slug || r.enabled === false) continue;
      if (typeof r.threshold === "number" && r.threshold > 0) {
        map.set(r.class_slug, r.threshold);
      }
    }
    setInterestThresholdBySlug(map);
  };


  useEffect(() => {
    // Track "updated N ago" per slug from DB rows.
    const m = new Map<string, string>();
    for (const r of dbRows) {
      const slug = r.slug ?? r.id;
      const created = (r as unknown as { created_at?: string }).created_at;
      if (created) m.set(slug, created);
    }
    setUpdatedAtBySlug(m);
  }, [dbRows]);

  // Auth redirects are handled once, by AdminGuard. A page-level redirect here
  // fired during token refreshes and kicked admins off their current page.






  useEffect(() => {
    if (user && isAdmin) {
      loadClasses();
      loadAssignments();
      loadCounts();
      loadInstructorNames();
      loadInterestThresholds();
    }

  }, [user, isAdmin, businessSettings.reportingStartDate, businessSettings.includeHistorical]);

  const merged = useMemo<MergedRow[]>(() => {
    const bySlug = new Map<string, MergedRow>();
    for (const item of staticClasses) {
      const row = fromStatic(item);
      bySlug.set(row.slug, row);
    }
    for (const row of dbRows) {
      const mapped = fromDb(row);
      const existing = bySlug.get(mapped.slug);
      if (existing) {
        bySlug.set(mapped.slug, { ...existing, ...mapped, source: "hybrid", legacy: existing.legacy });
      } else {
        bySlug.set(mapped.slug, mapped);
      }
    }
    return Array.from(bySlug.values()).sort((a, b) => a.title.localeCompare(b.title));
  }, [dbRows]);

  const counts = useMemo(() => {
    const c = Object.fromEntries(LIFECYCLE_STATUSES.map((s) => [s, 0])) as Record<VisibilityStatus, number>;
    for (const r of merged) c[registry.statusOf(r.slug)]++;
    return c;
  }, [merged, registry.entries]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return merged.filter((r) => {
      const status = registry.statusOf(r.slug);
      if (lifecycle === "all") {
        if (status === "archived") return false;
      } else if (lifecycle !== status) {
        return false;
      }
      if (!q) return true;
      return (
        r.title.toLowerCase().includes(q) ||
        (r.category ?? "").toLowerCase().includes(q) ||
        r.slug.toLowerCase().includes(q)
      );
    });
  }, [merged, query, lifecycle, registry.entries]);

  // Deep link: /admin/classes?focus=<slug-or-class-id> opens that class's
  // editor. Used by the read-only Staffing board's "Manage on Class" action and
  // Instructor Proposals links so assignments are always edited in the one
  // canonical place.
  const focusSlug = new URLSearchParams(window.location.search).get("focus");
  const [focusHandled, setFocusHandled] = useState(false);
  useEffect(() => {
    // Wait for the database load to finish: `merged` already contains static
    // classes synchronously, so matching before the DB rows arrive would
    // permanently miss database-only classes.
    if (focusHandled || !focusSlug || loading) return;
    const row = merged.find((r) => r.slug === focusSlug || r.dbId === focusSlug || r.id === focusSlug);
    if (row) setEditing(row);
    setFocusHandled(true);
  }, [focusSlug, merged, focusHandled, loading]);



  const setStatus = async (row: MergedRow, next: VisibilityStatus) => {
    const { error } = await upsertRegistryForSlug(row.slug, { visibility_status: next });
    if (error) {
      friendlyError({ what: "I couldn't update that class's status.", technical: error });
      return;
    }
    toast.success(`${row.title} → ${STATUS_META[next].label}`);
    void registry.refresh();
  };

  const copyPublicLink = async (row: MergedRow) => {
    const url = `${window.location.origin}/classes?open=${encodeURIComponent(row.slug)}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Public link copied");
    } catch {
      toast.error("Could not copy link");
    }
  };

  if (authLoading || !user || !isAdmin) {
    return (
      <main className="min-h-screen bg-background p-6">
        <p className="text-sm text-muted-foreground">Loading…</p>
      </main>
    );
  }

  const filterTabs: Array<{ key: LifecycleFilter; label: string; count: number | null }> = [
    { key: "all", label: "All Active", count: merged.length - counts.archived },
    ...LIFECYCLE_STATUSES.map((s) => ({ key: s as LifecycleFilter, label: STATUS_META[s].label, count: counts[s] })),
  ];

  return (
    <main className="min-h-screen bg-background">
      <div className="container mx-auto max-w-7xl px-4 md:px-6 py-6 space-y-4">
        <UnseenReviewBar
          badge="classes"
          note="Marking these read only clears the sidebar bubble — classes still need an instructor assigned."
        />

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="font-heading text-2xl font-semibold tracking-normal text-foreground">Classes</h1>
            <p className="text-sm text-muted-foreground">
              {loading
                ? "Loading class list…"
                : `${merged.length} classes · ${counts.published} published · ${counts.waitlist} waitlisted · ${counts.hidden} hidden`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search classes"
              className="h-9 w-56"
            />
            <Button variant="outline" size="sm" onClick={() => { loadClasses(); loadAssignments(); loadCounts(); loadInstructorNames(); loadInterestThresholds(); }} disabled={loading}>
              <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button size="sm" onClick={() => setCreating({ seed: {} })}>
              <Plus className="mr-2 h-4 w-4" />
              New Class
            </Button>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {(["classes", "instructors"] as const).map((v) => (
            <button
              key={v}
              onClick={() => setView(v)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${
                view === v
                  ? "bg-secondary text-secondary-foreground border-border"
                  : "bg-card text-muted-foreground border-border hover:bg-accent"
              }`}
            >
              {v === "classes" ? "Class board" : "By instructor"}
            </button>
          ))}
          <span className="text-[11px] text-muted-foreground">
            Instructor assignments are managed on the class — open a class and use its Instructor tab.
          </span>
        </div>

        {view === "instructors" ? (
          <InstructorSideView
            onManageOnClass={(slug) => {
              const row = merged.find((r) => r.slug === slug);
              if (row) setEditing(row);
            }}
          />
        ) : (
          <>
          <div className="flex flex-wrap gap-2">
            {filterTabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setLifecycle(tab.key)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                  lifecycle === tab.key
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-card text-muted-foreground border-border hover:bg-accent"
                }`}
              >
                {tab.label}
                {tab.count != null ? <span className="ml-1.5 opacity-70">({tab.count})</span> : null}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap gap-2">
            {(Object.keys(FOCUS_LABELS) as FocusFilter[]).map((k) => (
              <button
                key={k}
                onClick={() => setFocus(k)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                  focus === k
                    ? "bg-amber-100 text-amber-900 border-amber-300"
                    : "bg-card text-muted-foreground border-border hover:bg-accent"
                }`}
              >
                {FOCUS_LABELS[k]}
              </button>
            ))}
          </div>

          <FutureSessionInterestOverview
            onOpenClass={(classId) => {
              const row = merged.find((r) => r.dbId === classId);
              if (row) setEditing(row);
            }}
          />

          <PricingHealthCard
            classes={merged.map((r): ClassItem => ({
              id: r.slug,
              title: r.title,
              description: r.description ?? "",
              ages: r.ages ?? "—",
              schedule: r.schedule ?? "",
              duration: "",
              price: r.price ?? "",
              unitPrice: Number(r.pricePerChild) || 0,
              startDate: "",
              status: "open",
              image: r.thumbnail ?? "",
              category: r.category ?? "Class",
              billingUnit: coerceBillingUnit(r.billingUnit),
              managementType: r.managementType,
              dbId: r.dbId,
              hasInstructor: r.managementType === "external_provider" ? true : !!r.instructorId,
              priceDisplayRaw: r.priceDisplayRaw ?? null,
              displayOmitsUnitApproved: !!r.displayOmitsUnitApproved,
            }))}
            onOpenClass={(item) => {
              const src = merged.find((r) => r.slug === item.id);
              if (src) setEditing(src);
            }}
            onReload={() => {
              loadClasses();
            }}
          />


          {(() => {
            // Sum counts across every alias id (slug + uuid + db:uuid) so
            // registrations stored under either form are always included.
            // Single operational instructor answer per class card.
            const resolveCardInstructorFor = (src: MergedRow) => {
              const rows =
                (src.dbId ? assignments.get(src.dbId) : undefined) ?? assignments.get(src.slug);
              // Anchor on the class's canonical current/next session FIRST, then
              // pick an assignment that belongs to that session.
              const canonical = resolveNextSessionOption({
                sessions: src.sessions ?? src.legacy?.sessions ?? null,
                scheduleText: src.schedule ?? null,
                managementType: src.managementType,
                visibilityStatus: registry.statusOf(src.slug),
              });
              return resolveCardInstructor(
                src,
                rows,
                { label: canonical?.label ?? null, start: canonical?.start ?? null, end: canonical?.end ?? null },
                instructorNames,
              );
            };

            const readCounts = (primary: string | null | undefined): BoardCounts => {
              const aliases = getClassAliases(primary ?? "");
              const acc: BoardCounts = {
                enrolled: 0, waitlist: 0, interest: 0, revenueCents: 0,
                financeEnrolled: 0, financeRevenueCents: 0,
                nextSessionLabel: null, updatedAt: null,
              };
              for (const key of aliases) {
                const c = countsByKey.get(key);
                if (!c) continue;
                acc.enrolled += c.enrolled;
                acc.waitlist += c.waitlist;
                acc.interest += c.interest;
                acc.revenueCents += c.revenueCents;
                acc.financeEnrolled = (acc.financeEnrolled ?? 0) + (c.financeEnrolled ?? 0);
                acc.financeRevenueCents = (acc.financeRevenueCents ?? 0) + (c.financeRevenueCents ?? 0);
              }
              return acc;
            };

            // "What needs me today" lenses. Every predicate is derived from the
            // same canonical resolvers the cards already render from — no new
            // source of truth, no writes.
            const focusRows = (boardRows: BoardRow[]): BoardRow[] => {
              if (focus === "all") return boardRows;
              return boardRows.filter((row) => {
                const src = filtered.find((r) => r.slug === row.slug);
                if (!src) return false;
                const status = registry.statusOf(row.slug);
                const instructorState = resolveCardInstructorFor(src).state as BoardInstructorState;
                if (focus === "instructor_needed") {
                  return instructorState === "missing" && status !== "archived" && status !== "hidden";
                }
                if (focus === "attention") {
                  const readiness = readinessForRow(row, status, instructorState);
                  return readiness.critical > 0 || readiness.warnings > 0;
                }
                if (focus === "low_enrollment") {
                  const min = src.minEnrollment ?? 0;
                  if (min <= 0) return false;
                  if ((src.pricingModel ?? "paid") !== "paid") return false;
                  const op = resolveOperationalEnrollment(
                    enrollmentData.payments,
                    enrollmentData.children,
                    src.dbId ?? src.slug,
                    {
                      sessions: src.sessions ?? src.legacy?.sessions ?? null,
                      scheduleText: src.schedule ?? null,
                      managementType: src.managementType,
                      visibilityStatus: status,
                    },
                  );
                  return (op?.paidEnrolled ?? 0) < min;
                }
                if (focus === "pricing_issue") {
                  return (
                    auditClassPricing({
                      id: src.slug,
                      title: src.title,
                      description: src.description ?? "",
                      ages: src.ages ?? "—",
                      schedule: src.schedule ?? "",
                      duration: "",
                      price: src.price ?? "",
                      unitPrice: Number(src.pricePerChild) || 0,
                      startDate: "",
                      status: "open",
                      image: src.thumbnail ?? "",
                      category: src.category ?? "Class",
                      billingUnit: coerceBillingUnit(src.billingUnit),
                      managementType: src.managementType,
                      dbId: src.dbId,
                      hasInstructor:
                        src.managementType === "external_provider" ? true : !!src.instructorId,
                      priceDisplayRaw: src.priceDisplayRaw ?? null,
                      displayOmitsUnitApproved: !!src.displayOmitsUnitApproved,
                    }).length > 0
                  );
                }
                return true;
              });
            };

            return (
              <>
                <FinancialReportsNavCard />



                {loading ? (
                  <div className="rounded-lg border border-dashed border-border bg-card/50 p-10 text-center text-sm text-muted-foreground">
                    Loading classes…
                  </div>
                ) : (
                  <OperationsBoard
                    rows={focusRows(filtered.map((r): BoardRow => ({
                      slug: r.slug,
                      title: r.title,
                      category: r.category,
                      ages: r.ages,
                      thumbnail: r.thumbnail ?? null,
                      dbId: r.dbId,
                       capacity: r.capacity,
                       minEnrollment: r.minEnrollment ?? null,
                       managementType: r.managementType,
                       pricingModel: r.pricingModel ?? "paid",
                       scheduleModel: r.scheduleModel ?? "fixed",
                      source: r.source,
                      gate: r.dbId
                        ? {
                            status: r.dbStatus ?? "draft",
                            hidden: !!r.dbHidden,
                            accepts_registrations: r.acceptsRegistrations !== false,
                          }
                        : null,

                      registrationEnabled: registry.isRegistrationEnabled(r.slug),
                      calendarVisible: registry.entries.get(r.slug)?.calendar_visible ?? null,

                      schedule: r.schedule ?? null,
                      // Left `undefined` for legacy rows on purpose: unknown is
                      // not the same as missing, and must never be flagged.
                      location: r.location,
                      room: r.room,
                      days: r.preferredDays,
                      time: r.preferredTime,
                      pricePerChild: r.pricePerChild ?? null,
                      hasSession: !!resolveNextSessionLabel({
                        sessions: r.sessions ?? r.legacy?.sessions ?? null,
                        scheduleText: r.schedule ?? null,
                        managementType: r.managementType,
                        visibilityStatus: registry.statusOf(r.slug),

                      }),
                    })))}


                    query=""
                    statusOf={(slug) => registry.statusOf(slug)}
                    instructorStateFor={(row) => {
                      const src = filtered.find((r) => r.slug === row.slug);
                      if (!src) return "missing";
                      return resolveCardInstructorFor(src).state as BoardInstructorState;
                    }}
                    instructorNameFor={(row) => {
                      const src = filtered.find((r) => r.slug === row.slug);
                      if (!src) return null;
                      return resolveCardInstructorFor(src).name;
                    }}
                    instructorNoteFor={(row) => {
                      const src = filtered.find((r) => r.slug === row.slug);
                      if (!src) return null;
                      return resolveCardInstructorFor(src).note;
                    }}

                    countsFor={(row) => {
                      const c = readCounts(row.dbId ?? row.slug);
                      const src = filtered.find((r) => r.slug === row.slug);
                      const resolveInput = {
                        sessions: src?.sessions ?? src?.legacy?.sessions ?? null,
                        scheduleText: src?.schedule ?? null,
                        managementType: src?.managementType,
                        visibilityStatus: src ? registry.statusOf(src.slug) : null,
                      };
                      const nextSessionLabel = src ? resolveNextSessionLabel(resolveInput) : null;
                      // Canonical operational count: current/upcoming session only.
                      const op = src
                        ? resolveOperationalEnrollment(
                            enrollmentData.payments,
                            enrollmentData.children,
                            src.dbId ?? src.slug,
                            resolveInput,
                          )
                        : null;
                      return {
                        enrolled: op?.enrolled ?? 0,
                        paidEnrolled: op?.paidEnrolled ?? 0,
                        historical: op?.historical ?? 0,
                        sessionLabel: op?.sessionLabel ?? null,
                        waitlist: 0,
                        // Canonical interest = distinct FAMILIES, de-duplicated
                        // across every alias of the class and across repeat
                        // submissions by the same email.
                        interest: src
                          ? Math.max(
                              interestFamiliesFor(enrollmentData.interest, src.dbId ?? src.slug),
                              interestFamiliesFor(enrollmentData.interest, src.slug),
                            )
                          : c.interest,
                        revenueCents: c.revenueCents,
                        nextSessionLabel,
                        updatedAt: updatedAtBySlug.get(row.slug) ?? null,
                      };
                    }}

                    onEdit={(row) => {
                      const src = filtered.find((r) => r.slug === row.slug);
                      if (src) setEditing(src);
                    }}
                    onDuplicate={(row) => {
                      const src = filtered.find((r) => r.slug === row.slug);
                      if (src) setDuplicating(src);
                    }}
                    onSetStatus={(row, s) => {
                      const src = filtered.find((r) => r.slug === row.slug);
                      if (src) setStatus(src, s);
                    }}
                    onCreateNew={() => setCreating({ seed: {} })}
                  />
                )}
              </>
            );
          })()}
          </>
        )}
      </div>


      <ClassEditorDialog
        row={
          editing
            ? ({
                slug: editing.slug,
                title: editing.title,
                dbId: editing.dbId,
                legacyPreview: editing.dbId
                  ? undefined
                  : {
                      category: editing.category,
                      ages: editing.ages,
                      schedule: editing.schedule,
                      price: editing.price,
                      description: editing.description ?? null,
                      thumbnail: editing.thumbnail ?? null,
                    },
              } satisfies ClassEditorRow)
            : null
        }
        open={!!editing}
        onClose={() => setEditing(null)}
        onSaved={() => {
          loadClasses(); loadAssignments(); loadCounts(); loadInstructorNames(); loadInterestThresholds(); void registry.refresh();
        }}
      />

      {/* Create / Duplicate uses the same editor in create mode */}
      <ClassEditorDialog
        row={
          creating
            ? ({
                slug: "",
                title: creating.seed.title ?? "",
                mode: "create",
                seed: creating.seed,
                sourceLabel: creating.sourceLabel,
                copyRegistrationPeriodsFromSlug: creating.copyRegistrationPeriodsFromSlug,
              } satisfies ClassEditorRow)
            : null
        }
        open={!!creating}
        onClose={() => setCreating(null)}
        onSaved={() => {
          loadClasses(); loadAssignments(); loadCounts(); loadInstructorNames(); loadInterestThresholds(); void registry.refresh();
        }}
      />

      <DuplicateDialog
        source={duplicating}
        onCancel={() => setDuplicating(null)}
        onConfirm={async (cfg) => {
          const src = duplicating!;
          setDuplicating(null);
          let dbSrc: Record<string, unknown> | null = null;
          if (src.dbId) {
            const { data } = await (supabase as unknown as { from: (t: string) => { select: (c: string) => { eq: (k: string, v: string) => { maybeSingle: () => Promise<{ data: Record<string, unknown> | null }> } } } })
              .from("classes")
              .select(CLASS_SAFE_COLUMNS)
              .eq("id", src.dbId)
              .maybeSingle();
            dbSrc = data;
          }
          const supplies = (dbSrc?.supplies as { required?: unknown[]; recommended?: unknown[]; note?: string | null } | null) ?? null;
          const seed: Partial<DraftSeed> = {
            title: cfg.title,
            slug: cfg.slug,
            category: (dbSrc?.category as string | null) ?? src.category ?? "",
            ages: (dbSrc?.ages as string | null) ?? src.ages ?? "",
            short_description: (dbSrc?.short_description as string | null) ?? "",
            description: (dbSrc?.description as string | null) ?? src.description ?? "",
            thumbnail_url: cfg.reuseImages ? ((dbSrc?.thumbnail_url as string | null) ?? src.thumbnail ?? null) : null,
            visibility_status: "draft",
            accepts_registrations: false,
            calendar_visible: false,
            registration_enabled: false,
          };
          if (cfg.copySchedule && dbSrc) {
            Object.assign(seed, {
              schedule: (dbSrc.schedule as string | null) ?? "",
              preferred_days: (dbSrc.preferred_days as string | null) ?? "",
              preferred_time: (dbSrc.preferred_time as string | null) ?? "",
              class_length: (dbSrc.class_length as string | null) ?? "",
              start_date: (dbSrc.start_date as string | null) ?? "",
              duration: (dbSrc.duration as string | null) ?? "",
              sessions: (dbSrc.sessions as DraftSeed["sessions"]) ?? [],
              capacity: (dbSrc.capacity as number) ?? 12,
              min_enrollment: (dbSrc.min_enrollment as number) ?? 0,
            });
          }
          if (cfg.copyPricing && dbSrc) {
            Object.assign(seed, {
              price_per_child: dbSrc.price_per_child != null ? String(dbSrc.price_per_child) : "",
              price_display: (dbSrc.price_display as string | null) ?? "",
              billing_unit: (["per_session","per_month","per_semester"].includes(String(dbSrc.billing_unit)) ? (dbSrc.billing_unit as "per_session" | "per_month" | "per_semester") : "per_session"),
            });
          }
          if (cfg.copySupplies && supplies) {
            Object.assign(seed, {
              supplies_required: (supplies.required as DraftSeed["supplies_required"]) ?? [],
              supplies_recommended: (supplies.recommended as DraftSeed["supplies_recommended"]) ?? [],
              supplies_note: supplies.note ?? "",
            });
          }
          setCreating({
            sourceLabel: src.title,
            copyRegistrationPeriodsFromSlug: cfg.copyRegPeriods ? src.slug : undefined,
            seed,
          });
        }}
      />

    </main>
  );
}


/* ---------------- Read-only instructor-side view ----------------
   Moved here from /admin/staffing so "who is teaching what" lives on the one
   Classes destination. Reads the SAME canonical instructor_class_assignments
   rows; it never writes an assignment — every action deep-links to the Class
   Editor's Instructor tab. */
function InstructorSideView({ onManageOnClass }: { onManageOnClass: (slug: string) => void }) {
  const { classes: staffClasses, instructors, assignments, loading } = useStaffingBoard();
  const [q, setQ] = useState("");
  const term = q.trim().toLowerCase();

  const liveAssignments = useMemo(() => assignments.filter(isLiveAssignment), [assignments]);
  const visibleInstructors = useMemo(
    () =>
      instructors
        .filter((i) =>
          ["active", "approved", "prospect", "volunteer", "community_partner"].includes(i.status),
        )
        .filter(
          (i) => !term || `${i.full_name} ${i.preferred_name ?? ""}`.toLowerCase().includes(term),
        ),
    [instructors, term],
  );

  return (
    <div className="space-y-4">
      <Alert>
        <Info className="h-4 w-4" />
        <AlertTitle>Read-only view</AlertTitle>
        <AlertDescription className="text-xs">
          To assign, change or end an instructor, open the class and use its Instructor tab.
        </AlertDescription>
      </Alert>
      <Input
        className="h-9 max-w-xs"
        placeholder="Search instructors"
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {visibleInstructors.map((i) => (
            <InstructorStaffingCard
              key={i.id}
              instructor={i}
              assignments={liveAssignments.filter((a) => a.instructor_id === i.id)}
              pastAssignments={assignments.filter(
                (a) => a.instructor_id === i.id && !isLiveAssignment(a),
              )}
              classes={staffClasses}
              onManageOnClass={(row: AssignmentRow) => {
                const cls = staffClasses.find(
                  (c) => c.id === row.class_id || c.slug === row.class_slug,
                );
                const slug = cls?.slug ?? row.class_slug;
                if (slug) onManageOnClass(slug);
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- Duplicate pre-dialog ---------------- */

type DupCfg = {
  title: string;
  slug: string;
  reuseImages: boolean;
  copySchedule: boolean;
  copyPricing: boolean;
  copySupplies: boolean;
  copyRegPeriods: boolean;
};

function DuplicateDialog({
  source,
  onCancel,
  onConfirm,
}: {
  source: MergedRow | null;
  onCancel: () => void;
  onConfirm: (cfg: DupCfg) => void;
}) {
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [reuseImages, setReuseImages] = useState(true);
  const [copySchedule, setCopySchedule] = useState(true);
  const [copyPricing, setCopyPricing] = useState(true);
  const [copySupplies, setCopySupplies] = useState(true);
  const [copyRegPeriods, setCopyRegPeriods] = useState(false);

  useEffect(() => {
    if (source) {
      const t = `${source.title} (Copy)`;
      setTitle(t);
      setSlug(slugify(t));
      setReuseImages(true);
      setCopySchedule(true);
      setCopyPricing(true);
      setCopySupplies(true);
      setCopyRegPeriods(false);
    }
  }, [source]);

  if (!source) return null;
  const canConfirm = title.trim().length > 2 && slug.trim().length > 2;
  return (
    <Dialog open={!!source} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Duplicate class</DialogTitle>
          <DialogDescription>
            Copying <strong>{source.title}</strong>. A new draft will be created with no registrations,
            payments, or instructor assignments.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>New title</Label>
            <Input value={title} onChange={(e) => { setTitle(e.target.value); setSlug(slugify(e.target.value)); }} />
          </div>
          <div>
            <Label>New slug</Label>
            <Input value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))} />
          </div>
          <div className="space-y-2 pt-2 border-t">
            <div className="text-sm font-medium">What to copy</div>
            <label className="flex items-center gap-2 text-sm"><Checkbox checked={reuseImages} onCheckedChange={(v) => setReuseImages(!!v)} /> Reuse images</label>
            <label className="flex items-center gap-2 text-sm"><Checkbox checked={copySchedule} onCheckedChange={(v) => setCopySchedule(!!v)} /> Schedule / session templates</label>
            <label className="flex items-center gap-2 text-sm"><Checkbox checked={copyPricing} onCheckedChange={(v) => setCopyPricing(!!v)} /> Pricing</label>
            <label className="flex items-center gap-2 text-sm"><Checkbox checked={copySupplies} onCheckedChange={(v) => setCopySupplies(!!v)} /> Supplies</label>
            <label className="flex items-center gap-2 text-sm"><Checkbox checked={copyRegPeriods} onCheckedChange={(v) => setCopyRegPeriods(!!v)} /> Registration-period links</label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onCancel}>Cancel</Button>
          <Button
            disabled={!canConfirm}
            onClick={() => onConfirm({ title: title.trim(), slug: slug.trim(), reuseImages, copySchedule, copyPricing, copySupplies, copyRegPeriods })}
          >
            Continue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* ---------------- Row helpers ---------------- */

function InstructorPill({ state }: { state: InstructorState }) {
  const styles: Record<InstructorState, { label: string; cls: string; Icon: typeof Users }> = {
    assigned: { label: "Assigned", cls: "bg-emerald-100 text-emerald-800 border-emerald-300", Icon: UserCheck },
    missing: { label: "Needs Instructor", cls: "bg-amber-100 text-amber-900 border-amber-300", Icon: UserX },
    multiple: { label: "Multiple", cls: "bg-sky-100 text-sky-800 border-sky-300", Icon: Users },
    pending: { label: "Pending Acceptance", cls: "bg-purple-100 text-purple-800 border-purple-300", Icon: AlertCircle },
    external: { label: "Privately Managed", cls: "bg-sky-100 text-sky-800 border-sky-300", Icon: Users },
  };
  const s = styles[state];
  return (
    <Badge variant="outline" className={`${s.cls} font-medium`}>
      <s.Icon className="h-3 w-3 mr-1" />
      {s.label}
    </Badge>
  );
}

function RowActions({
  row,
  status,
  onEdit,
  onStatus,
  onCopyLink,
  onPreview,
  onDuplicate,
}: {
  row: MergedRow;
  status: VisibilityStatus;
  onEdit: () => void;
  onStatus: (s: VisibilityStatus) => void;
  onCopyLink: () => void;
  onPreview: () => void;
  onDuplicate: () => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="icon" variant="ghost" className="h-8 w-8">
          <MoreVertical className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuItem onClick={onEdit}>Edit</DropdownMenuItem>
        <DropdownMenuItem onClick={onPreview}>
          <ExternalLink className="h-4 w-4 mr-2" /> Preview as parent
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onCopyLink}>
          <Copy className="h-4 w-4 mr-2" /> Copy public link
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={onDuplicate} disabled={row.source === "legacy"}>
          <Files className="h-4 w-4 mr-2" /> Duplicate
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {status !== "published" && (
          <DropdownMenuItem onClick={() => onStatus("published")}>
            <Eye className="h-4 w-4 mr-2" /> Publish
          </DropdownMenuItem>
        )}
        {status !== "waitlist" && (
          <DropdownMenuItem onClick={() => onStatus("waitlist")}>
            Move to waitlist
          </DropdownMenuItem>
        )}
        {status !== "hidden" && (
          <DropdownMenuItem onClick={() => onStatus("hidden")}>
            <EyeOff className="h-4 w-4 mr-2" /> Hide
          </DropdownMenuItem>
        )}
        {status !== "archived" && (
          <DropdownMenuItem onClick={() => onStatus("archived")}>
            <Archive className="h-4 w-4 mr-2" /> Archive
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/* ---------------- Root export ---------------- */

export default function ClassesPage() {
  return (
    <AdminErrorBoundary label="Classes workspace">
      <ClassesPageInner />
    </AdminErrorBoundary>
  );
}

